import { useRef, useState } from "react";
import styles from "../styles/runner.module.css";
import type { PhotoUploadConfig } from "../types";

interface Props {
  prompt: string;
  config: PhotoUploadConfig;
  passingScore: number;
  onScored: (result: {
    score: number;
    passed: boolean;
    raw: { has_photo: boolean; mime: string | null };
  }) => void;
}

/**
 * MVP photo upload: accepts any image, immediately reports a pass with
 * score 1.0 if a photo was selected.
 *
 * In Phase 2 this widget will:
 *   1) Upload to a presigned URL.
 *   2) Wait for the server's ML classifier (CoreML/Claude Vision).
 *   3) Return the score from the classifier.
 *
 * For now it exists to prove the runner can host *any* widget, including
 * one that touches the device camera roll.
 */
export function PhotoUpload({ prompt, config, passingScore, onScored }: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [mime, setMime] = useState<string | null>(null);

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setMime(f.type);
    setPreviewUrl(URL.createObjectURL(f));
  }

  function submit() {
    if (submitted || !previewUrl) return;
    // MVP: server-side ML pending; trust the upload happened.
    setSubmitted(true);
    onScored({
      score: 1.0,
      passed: 1.0 >= passingScore,
      raw: { has_photo: true, mime },
    });
  }

  return (
    <div className={styles.col}>
      <h2 className={styles.prompt}>{prompt}</h2>
      <p style={{ color: "#4b5563", margin: 0 }}>{config.instructions}</p>
      <p style={{ color: "#6b7280", margin: 0, fontSize: 13 }}>
        Clases aceptadas por el clasificador: {config.accepted_classes.join(", ")}.
      </p>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFile}
        style={{ display: "none" }}
        disabled={submitted}
      />
      <button
        type="button"
        className={styles.secondaryButton}
        onClick={() => inputRef.current?.click()}
        disabled={submitted}
      >
        {previewUrl ? "Cambiar foto" : "Tomar / elegir foto"}
      </button>

      {previewUrl && (
        <img
          src={previewUrl}
          alt="Vista previa"
          style={{ width: "100%", maxHeight: 320, objectFit: "cover", borderRadius: 12 }}
        />
      )}

      {!submitted ? (
        <button
          type="button"
          className={styles.primaryButton}
          disabled={!previewUrl}
          onClick={submit}
        >
          Subir foto
        </button>
      ) : (
        <div className={styles.summary}>
          <span className={styles.scoreBadge}>OK</span>
          <strong>Foto recibida</strong>
          <span>
            En Fase 2 un clasificador validará el contenido del plato. Por ahora se
            registra el envío.
          </span>
        </div>
      )}
    </div>
  );
}
